/**
 * Transform WordPress "nav_menu_item" flat list into the menuData structure expected by Nav2025 components.
 *
 * Input examples (items):
 *   [ { ID, menu_item_parent, title, url, menu_order, ... }, ... ]
 * Or full object from props.sitemegamenu:
 *   { sitemegamenu: [...], sitemegamenucontact: [...], sitemegamenumuotolevy: [...], ... }
 *
 * Output (array):
 *   [
 *     { id, title, url, hasSubmenu, submenu?: [ { id, title, url, children?: [...] }, ... ] },
 *     ...
 *   ]
 */

/**
 * Safely coerce potential numeric string to number; returns 0 for null/"0".
 */
function toNumber(value) {
  if (value === null || value === undefined) return 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Normalize a single raw WP item to an internal node with linkage fields.
 */
function normalizeItem(raw) {
  const id = toNumber(raw.ID ?? raw.db_id ?? raw.id);
  const parentId = toNumber(raw.menu_item_parent);
  const title = (raw.title ?? raw.post_title ?? "").trim();
  const url = raw.url ?? "";
  const order = toNumber(raw.menu_order);
  // Custom behaviour from WP (added in header.php). Expect 'cta' | 'default' | undefined
  const behaviour = raw.behaviour || raw.menu_item_behaviour || undefined;
  // Custom layout from WP: 'horizontal' | 'vertical' (applies to 2nd level items only)
  const layout = raw.layout || raw.menu_item_layout || undefined;
  return {
    id,
    parentId,
    title,
    url,
    order,
    behaviour,
    layout,
    raw,
    children: [],
  };
}

/**
 * Build a tree from a flat list. Returns sorted root nodes.
 */
function buildTree(items) {
  const map = new Map();
  items.forEach((it) => {
    const node = normalizeItem(it);
    map.set(node.id, node);
  });

  // Link children
  map.forEach((node) => {
    if (node.parentId && map.has(node.parentId)) {
      map.get(node.parentId).children.push(node);
    }
  });

  // Sort children by menu_order
  map.forEach((node) => {
    if (node.children.length > 0) {
      node.children.sort((a, b) => a.order - b.order);
    }
  });

  // Return roots sorted by menu_order
  return Array.from(map.values())
    .filter((n) => !n.parentId)
    .sort((a, b) => a.order - b.order);
}

/**
 * Map a node (level 1) to top-level menuData item.
 */
function mapTopLevel(node) {
  const hasChildren = node.children && node.children.length > 0;

  return {
    id: node.id,
    title: node.title || String(node.id),
    url: node.url || "#",
    hasSubmenu: hasChildren,
    ...(hasChildren
      ? { submenu: node.children.map(mapSecondLevelWithOptions) }
      : {}),
  };
}

/**
 * Map a node (level 2) to submenu entry in menuData. If it has children, expose as `children`.
 */
function mapSecondLevel(node) {
  const hasChildren = node.children && node.children.length > 0;
  const base = {
    id: node.id,
    title: node.title || String(node.id),
    url: node.url || "#",
  };

  if (!hasChildren) return base;

  return {
    ...base,
    children: node.children.map((c) => ({
      id: c.id,
      title: c.title || String(c.id),
      url: c.url || "#",
    })),
  };
}

/**
 * Group second-level nodes into sections based on their behaviour.
 * - If behaviour === 'cta': section becomes { id, type: 'buttons', children: [{id,title,url,style}] }
 *   - style mapping: default to 'primary'. If WP needs to control ghost/primary per child later, extend meta.
 * - Otherwise: map using standard section mapping (title/url + optional children).
 */
function groupSecondLevelByBehaviour(nodes) {
  if (!Array.isArray(nodes) || nodes.length === 0) return [];

  // We treat each node independently as a "section". Behaviour must be uniform inside node's own children.
  return nodes.map((node) => {
    const behaviour = (node.behaviour || "").toString().toLowerCase();
    if (behaviour === "cta") {
      // Render this whole section as buttons. If the node has children, render those as buttons
      // else render the node itself as a single button.
      const buttons = (
        node.children && node.children.length > 0 ? node.children : [node]
      ).map((c) => ({
        id: c.id,
        title: c.title || String(c.id),
        url: c.url || "#",
        style: "primary",
      }));
      return { id: node.id, type: "buttons", children: buttons };
    }

    // Default behaviour: regular section
    return mapSecondLevel(node);
  });
}

/**
 * Map a second-level item considering behaviour (CTA) and layout (vertical sections vs horizontal children).
 * Priority: CTA overrides layout.
 */
function mapSecondLevelWithOptions(node) {
  const behaviour = (node.behaviour || "").toString().toLowerCase();
  if (behaviour === "cta") {
    const buttons = (
      node.children && node.children.length > 0 ? node.children : [node]
    ).map((c) => ({
      id: c.id,
      title: c.title || String(c.id),
      url: c.url || "#",
      style: "primary",
    }));
    return { id: node.id, type: "buttons", children: buttons };
  }

  const layout = (node.layout || node.raw?.layout || "horizontal")
    .toString()
    .toLowerCase();
  const base = {
    id: node.id,
    title: node.title || String(node.id),
    url: node.url || "#",
  };

  if (layout === "vertical") {
    // Represent children as sections
    const sections = (node.children || []).map((sec) => ({
      id: sec.id,
      title: sec.title || String(sec.id),
      url: sec.url || "#",
      ...(sec.children && sec.children.length
        ? {
            children: sec.children.map((leaf) => ({
              id: leaf.id,
              title: leaf.title || String(leaf.id),
              url: leaf.url || "#",
            })),
          }
        : {}),
    }));
    return { ...base, sections };
  }

  // Default horizontal: children list (3rd level)
  if (!node.children || node.children.length === 0) return base;
  return {
    ...base,
    children: node.children.map((c) => ({
      id: c.id,
      title: c.title || String(c.id),
      url: c.url || "#",
    })),
  };
}

/**
 * Public helper: Convert props.sitemegamenu to menuData array.
 * Accepts either the full object or a direct array of WP items.
 */
export function parseSitemegamenuToMenuData(input) {
  if (!input) return [];

  // Accept both: full object (with .sitemegamenu) or array of items
  const items = Array.isArray(input)
    ? input
    : Array.isArray(input.sitemegamenu)
      ? input.sitemegamenu
      : [];

  if (!items.length) return [];

  const roots = buildTree(items);
  return roots.map(mapTopLevel);
}

export default parseSitemegamenuToMenuData;
